// react
import { useCallback, useMemo, useState } from 'react';

// store
import { useUpdateIsOnboardedMutation } from 'store/api/iam';

// models
import { FieldType, QuestionID } from '@karehero/models';

// components
import { OnboardingActivatedStart } from '@karehero/llama/components/organisms/OnboardingActivatedStart/OnboardingActivatedStart';
import {
  OnboardingEditor,
  OnboardingEditorProps,
} from '@karehero/llama/components/organisms/OnboardingEditor/OnboardingEditor';
import { OnboardingLoading } from '@karehero/llama/components/organisms/OnboardingLoading/OnboardingLoading';
import { OnboardingTypeform } from '@karehero/llama/components/organisms/OnboardingTypeform/OnboardingTypeform';

import {
  OnboardingActionType,
  OnboardingFlowConfig,
} from '@karehero/llama/components/pages/OnboardingFlow/OnboardingFlow';

// config
import { onboardingLoadingConfig } from 'config/onboarding';
import { useSelector } from 'store';
import { getAccount } from 'store/slices/iamSlice';

// capacitor
import { Capacitor } from '@capacitor/core';

interface UseOnboardingOptions {
  typeformID?: string;
  isPrimaryCaregiver?: boolean;
}

const validateAccountFirstName = (value?: any) => !!value;
const validateAccountLastName = (value?: any) => !!value;
const validateCareRecipientType = (value?: any) => !!value;
const validateCareRecipientHoursPerWeek = (value?: any) => !!value;
const validateCareRecipientPostcode = (value?: any) => {
  if (!value) return true;
  return !!String(value).match(
    /\b(?:[A-Za-z][A-HJ-Ya-hj-y]?[0-9][0-9A-Za-z]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})\b/,
  );
};
const validateCareRecipientSymptomsAndConditions = (value?: string) => {
  if (!value) return false;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
};

enum HoursPerWeek {
  Nothing = 'nothing',
  OneToTen = '1-10',
  ElevenToTwenty = '11-20',
  TwentyOneToThirty = '21-30',
  ThirtyOnePlus = '31+',
}

enum CareRecipientType {
  Self = 'self',
  SomeoneElse = 'someone-else',
  NoOne = 'no-one',
}

const careRecipientTypeOptions: { id: CareRecipientType; label: string }[] = [
  { id: CareRecipientType.SomeoneElse, label: 'Someone else' },
  { id: CareRecipientType.Self, label: 'Myself' },
  { id: CareRecipientType.NoOne, label: "No-one yet, I'm just exploring" },
];

const careRecipientHoursPerWeekOptions = [
  {
    value: HoursPerWeek.Nothing,
    label: 'Nothing at the moment',
  },
  {
    value: HoursPerWeek.OneToTen,
    label: '1-10 hours',
  },
  {
    value: HoursPerWeek.ElevenToTwenty,
    label: '11-20 hours',
  },
  {
    value: HoursPerWeek.TwentyOneToThirty,
    label: '21-30 hours',
  },
  {
    value: HoursPerWeek.ThirtyOnePlus,
    label: '31+ hours',
  },
];

enum CareRecipientSymptomsAndConditions {
  Arthritis = 'arthritis',
  Cancer = 'cancer',
  Dementia = 'dementia',
  Diabetes = 'diabetes',
  HeartDisease = 'heart-disease',
  Hypertension = 'hypertension',
  MentalHealth = 'mental-health',
  Stroke = 'stroke',
  RespiratoryIllness = 'respiratory-illness',
  NoSymptoms = 'no-symptoms',
}

const careRecipientSymptomsAndConditionsOptions = [
  { value: CareRecipientSymptomsAndConditions.Arthritis, label: 'Arthritis' },
  { value: CareRecipientSymptomsAndConditions.Cancer, label: 'Cancer' },
  { value: CareRecipientSymptomsAndConditions.Dementia, label: 'Dementia' },
  { value: CareRecipientSymptomsAndConditions.Diabetes, label: 'Diabetes' },
  {
    value: CareRecipientSymptomsAndConditions.HeartDisease,
    label: 'Heart Disease',
  },
  {
    value: CareRecipientSymptomsAndConditions.Hypertension,
    label: 'Hypertension',
  },
  {
    value: CareRecipientSymptomsAndConditions.MentalHealth,
    label: 'Mental Health',
  },
  { value: CareRecipientSymptomsAndConditions.Stroke, label: 'Stroke' },
  {
    value: CareRecipientSymptomsAndConditions.RespiratoryIllness,
    label: 'Respiratory Illness',
  },
  {
    value: CareRecipientSymptomsAndConditions.NoSymptoms,
    label: 'No Symptoms',
  },
];

export const useOnboarding = (options?: UseOnboardingOptions) => {
  // hooks
  const account = useSelector(getAccount);
  const [updateIsOnboarded] = useUpdateIsOnboardedMutation();
  const { isPrimaryCaregiver = true } = options ?? {};

  // states
  const [accountFirstName, setAccountFirstName] = useState<{ value: string }>({
    value: '',
  });
  const [accountLastName, setAccountLastName] = useState<{ value: string }>({
    value: '',
  });
  const [careRecipientFirstName, setCareRecipientFirstName] = useState<{
    value: string;
    isConsent: boolean;
  }>({ value: '', isConsent: false });
  const [careRecipientType, setCareRecipientType] = useState<{
    value: CareRecipientType | undefined;
  }>({ value: undefined });
  const [careRecipientHoursPerWeek, setCareRecipientHoursPerWeek] = useState<{
    value: HoursPerWeek | undefined;
  }>({ value: undefined });
  const [
    careRecipientSymptomsAndConditions,
    setCareRecipientSymptomsAndConditions,
  ] = useState<{ value: string }>({
    value: '[]',
  });
  const [careRecipientDateOfBirth, setCareRecipientDateOfBirth] = useState<{
    value: string;
  }>({ value: '' });
  const [careRecipientPostcode, setCareRecipientPostcode] = useState<{
    value: string;
  }>({ value: '' });

  // IDs in this map will be used as question IDs in the triage API.
  const responses: Record<QuestionID, string | boolean | undefined> = useMemo(
    () => ({
      'account-first-name-v1': accountFirstName.value,
      'account-last-name-v1': accountLastName.value,
      'account-preferred-name-v1': undefined,
      'care-recipient-first-name-v1': careRecipientFirstName.value,
      'care-recipient-last-name-v1': undefined,
      'care-recipient-type-v1': careRecipientType.value,
      'care-recipient-consent-v1': careRecipientFirstName.isConsent
        ? 'true'
        : 'false',
      'care-recipient-hours-per-week-v1': careRecipientHoursPerWeek.value,
      'care-recipient-symptoms-and-conditions-v1':
        careRecipientSymptomsAndConditions.value,
      'care-recipient-date-of-birth-v1': careRecipientDateOfBirth.value,
      'care-recipient-postcode-v1': careRecipientPostcode.value,
    }),
    [
      accountFirstName,
      accountLastName,
      careRecipientFirstName,
      careRecipientType,
      careRecipientHoursPerWeek,
      careRecipientSymptomsAndConditions,
      careRecipientDateOfBirth,
      careRecipientPostcode,
    ],
  );

  // memos
  const accountNamesQuestions = useMemo(() => {
    let accountNamesQuestions: OnboardingFlowConfig<OnboardingEditorProps>[] =
      [];
    if (!account.kindeUser) {
      return accountNamesQuestions;
    }

    if (!account.kindeUser.firstName || account.firstName === '') {
      accountNamesQuestions.push({
        id: 'first-name',
        Component: OnboardingEditor,
        isProgressBar: true,
        config: [
          {
            id: 'first-name',
            value: accountFirstName,
            title: "What's your first name?",
            subTitle: "We'll use this information to create your profile.",
            onChange: setAccountFirstName,
            fieldDef: {
              field: {
                type: FieldType.Text,
              },
              validation: validateAccountFirstName,
            },
          },
        ],
      });
    }
    if (!account.kindeUser.lastName || account.lastName === '') {
      accountNamesQuestions.push({
        id: 'last-name',
        Component: OnboardingEditor,
        isProgressBar: true,
        config: [
          {
            id: 'last-name',
            title: "What's your last name?",
            subTitle: "We'll use this information to create your profile.",
            value: accountLastName,
            onChange: setAccountLastName,
            fieldDef: {
              field: {
                type: FieldType.Text,
              },
              validation: validateAccountLastName,
            },
          },
        ],
      });
    }
    return accountNamesQuestions;
  }, [
    account,
    accountFirstName,
    accountLastName,
    setAccountFirstName,
    setAccountLastName,
  ]);

  const typeformConfig = useMemo(
    () => [
      {
        id: 'typeform-onboarding',
        Component: OnboardingTypeform,
        config: [
          {
            id: '1',
            formID: options?.typeformID,
            isNativeApp: Capacitor.isNativePlatform(),
            onSubmit:
              (handleClick: (actionType: OnboardingActionType) => void) =>
              () => {
                Capacitor.isNativePlatform()
                  ? undefined
                  : handleClick(OnboardingActionType.Next);
              },
            actions: null,
          },
        ],
      },
    ],
    [options],
  );

  const careRecipientTypeQuestions = useMemo(
    () => [
      {
        id: 'who-do-you-want-to-support',
        Component: OnboardingEditor,
        isProgressBar: true,
        config: [
          {
            id: 'care-recipient-type',
            title: `${account?.firstName || 'Caregiver'}, who do you want to support?`,
            subTitle:
              'Supporting more than one person? You’ll be able to add others later. ',
            value: careRecipientType,
            onChange: setCareRecipientType,
            fieldDef: {
              field: {
                type: FieldType.RadioGroup,
                variant: 'outlined',
                options: careRecipientTypeOptions.map((x) => ({
                  value: x.id,
                  label: x.label,
                })),
                size: 'md',
                maxColumns: 1,
                isCustom: false,
              },
              validation: validateCareRecipientType,
            },
          },
        ],
      },
    ],
    [
      careRecipientType,
      setCareRecipientType,
      careRecipientTypeOptions,
      account,
    ],
  );

  const validateCareRecipientFirstName = (value?: any) => !!value;

  const careRecipientQuestions = useMemo(() => {
    if (careRecipientType.value !== CareRecipientType.SomeoneElse) {
      return [];
    }

    return [
      {
        id: 'care-recipient-first-name',
        Component: OnboardingEditor,
        isProgressBar: true,
        config: [
          {
            id: 'first-name',
            title: "What's their first name?",
            subTitle: "We'll use this information to create their profile.",
            value: careRecipientFirstName,
            onChange: setCareRecipientFirstName,
            fieldDef: {
              field: {
                type: FieldType.Text,
              },
              validation: validateCareRecipientFirstName,
            },
            consent: {
              label:
                'I have consent, or Power of Attorney, to share sensitive information about the person to support.',
              value: careRecipientFirstName.isConsent,
              onChange: (value: boolean) =>
                setCareRecipientFirstName({
                  ...careRecipientFirstName,
                  isConsent: value,
                }),
            },
          },
        ],
      },
      {
        id: 'care-recipient-hours-per-week',
        Component: OnboardingEditor,
        isProgressBar: true,
        config: [
          {
            id: 'hours-per-week',
            title: `How much time do you spend supporting ${careRecipientFirstName.value} per week?`,
            subTitle:
              'This may include anything from scheduling appointments to giving care. It helps us calculate your Carer’s Allowance eligibility.',
            value: careRecipientHoursPerWeek,
            onChange: setCareRecipientHoursPerWeek,
            fieldDef: {
              field: {
                type: FieldType.Select,
                options: careRecipientHoursPerWeekOptions,
                size: 'md',
                maxColumns: 1,
                isCustom: false,
              },
              validation: validateCareRecipientHoursPerWeek,
            },
          },
        ],
      },
    ];
  }, [
    careRecipientType,
    careRecipientFirstName,
    setCareRecipientFirstName,
    careRecipientHoursPerWeek,
    setCareRecipientHoursPerWeek,
  ]);

  const careProfileQuestions = useMemo(() => {
    if (careRecipientType.value === CareRecipientType.NoOne) {
      return [];
    }

    return [
      {
        id: 'symptoms-and-conditions',
        Component: OnboardingEditor,
        isProgressBar: true,
        config: [
          {
            id: 'symptoms-and-conditions',
            title: `Which of the following are true for ${careRecipientType.value === CareRecipientType.Self ? 'you' : careRecipientFirstName.value}?`,
            subTitle: 'This helps us calculate eligibility for key benefits.',
            value: careRecipientSymptomsAndConditions,
            onChange: (newValue: { value: string }) => {
              setCareRecipientSymptomsAndConditions({ value: newValue.value });
            },
            fieldDef: {
              field: {
                type: FieldType.MultiSelectChip,
                options: careRecipientSymptomsAndConditionsOptions.map((x) => ({
                  value: x.value,
                  label: x.label,
                })),
                label: 'Select all that apply',
                size: 'md',
                maxColumns: 1,
                isCustom: true,
              },
              validation: validateCareRecipientSymptomsAndConditions,
            },
          },
        ],
      },
      {
        id: 'care-recipient-dob',
        Component: OnboardingEditor,
        isProgressBar: true,
        config: [
          {
            id: 'care-recipient-dob',
            title: `What's ${careRecipientType.value === CareRecipientType.Self ? 'your' : `${careRecipientFirstName.value}'s`} date of birth?`,
            subTitle:
              'This helps us calculate eligibility for funding and age-specific support.',
            value: careRecipientDateOfBirth,
            onChange: setCareRecipientDateOfBirth,
            fieldDef: {
              field: {
                type: FieldType.DatePicker,
                size: 'md',
                maxColumns: 1,
              },
            },
            actions: [
              {
                label: 'Back',
                onClick: OnboardingActionType.Back,
              },
              {
                label: careRecipientDateOfBirth.value ? 'Next' : 'Skip',
                onClick: OnboardingActionType.Next,
              },
            ],
          },
        ],
      },
      {
        id: 'care-recipient-postcode',
        Component: OnboardingEditor,
        isProgressBar: true,
        config: [
          {
            id: 'care-recipient-postcode',
            title: `Where ${careRecipientType.value === CareRecipientType.Self ? 'are you' : `is ${careRecipientFirstName.value}`} based?`,
            subTitle:
              'This helps us find the right support in the right location.',
            value: careRecipientPostcode,
            onChange: setCareRecipientPostcode,
            fieldDef: {
              field: {
                type: FieldType.Text,
              },
              iconLeft: 'map-marker-alt',
              placeholder: `Enter ${careRecipientType.value === CareRecipientType.Self ? 'your' : 'their'} postcode`,
              validation: validateCareRecipientPostcode,
            },
            actions: [
              {
                label: 'Back',
                onClick: OnboardingActionType.Back,
              },
              {
                label: careRecipientPostcode.value ? 'Next' : 'Skip',
                onClick: OnboardingActionType.Next,
              },
            ],
          },
        ],
      },
    ];
  }, [
    careRecipientType,
    careRecipientFirstName,
    setCareRecipientSymptomsAndConditions,
    careRecipientSymptomsAndConditions,
    careRecipientSymptomsAndConditionsOptions,
    validateCareRecipientSymptomsAndConditions,
    careRecipientDateOfBirth,
    setCareRecipientDateOfBirth,
    careRecipientPostcode,
    setCareRecipientPostcode,
  ]);

  const incompleteQuestionsConfig = useMemo(
    () => [
      {
        id: 'activated-start',
        Component: OnboardingActivatedStart,
        config: [
          {
            id: '1',
            title: 'Personalise Your Journey',
            text: [
              'Before scheduling a call with a care expert, we need to get to know you better.',
              'We’ll ask you a few questions about the loved one you want support for.',
              'Your answers will give you a bespoke action plan that solves your needs.',
            ],
            ctaButtonLabel: 'Personalise Now',
            actions: null,
            isIgnoreProgress: true,
          },
        ],
      },
    ],
    [],
  );

  const loadingConfig = useMemo(
    () => [
      {
        id: 'loading',
        Component: OnboardingLoading,
        config: onboardingLoadingConfig,
        onStart: (handleClick: (action: OnboardingActionType) => void) => {
          setTimeout(
            () => {
              handleClick(OnboardingActionType.Next);
            },
            1400 + Math.random() * 300,
          );
        },
      },
    ],
    [],
  );

  const primaryCaregiverQuestions = useMemo(
    () => [
      ...accountNamesQuestions,
      ...careRecipientTypeQuestions,
      ...careRecipientQuestions,
      ...careProfileQuestions,
    ],
    [
      accountNamesQuestions,
      careRecipientTypeQuestions,
      careRecipientQuestions,
      careProfileQuestions,
    ],
  );

  const secondaryCaregiverQuestions = useMemo(() => {
    // TODO[onboarding-v2]: confirm other secondary caregiver questions
    return [...accountNamesQuestions];
  }, [accountNamesQuestions]);

  const onboardingQuestions = useMemo(
    () =>
      isPrimaryCaregiver
        ? primaryCaregiverQuestions
        : secondaryCaregiverQuestions,
    [
      isPrimaryCaregiver,
      primaryCaregiverQuestions,
      secondaryCaregiverQuestions,
    ],
  );

  return {
    incompleteQuestionsConfig,
    loadingConfig,
    updateIsOnboarded,
    typeformConfig,
    responses,
    onboardingQuestions,
  };
};
